import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from 'lucide-react';
interface AboutPageProps {
  onNavigate: (page: string) => void;
}
const features = ['BYOB', 'Catering Services', 'Family-Friendly', 'Good for Groups', 'Online Ordering', 'Private Events', 'Solo Dining', 'Takeout Services'];
const team = [{
  name: 'Cynthia Odeka',
  role: 'Master Chef'
}, {
  name: 'Paul Trueman',
  role: 'Master Chef'
}, {
  name: 'Oscar Oldman',
  role: 'Master Chef'
}, {
  name: 'Emma Newman',
  role: 'Master Chef'
}];
export function AboutPage({
  onNavigate
}: AboutPageProps) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen bg-cream pb-20" data-id="element-234">
      {/* Hero */}
      <div className="bg-charcoal text-white py-24 relative overflow-hidden" data-id="element-235">
        <div className="absolute inset-0 bg-gradient-to-br from-charcoal via-[#3d1a0a] to-amber opacity-80" data-id="element-236" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-237">
          <h1 className="font-heading text-5xl md:text-7xl uppercase tracking-wide mb-6" data-id="element-238">
            Our <span className="text-amber" data-id="element-239">Story</span>
          </h1>
          <p className="font-body text-xl text-white/80 leading-relaxed" data-id="element-240">
            African food with a soul.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-241">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24" data-id="element-242">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="space-y-6" data-id="element-243">
            <h2 className="font-heading text-4xl text-charcoal uppercase tracking-wide" data-id="element-244">
              The Journey of Victory Kitchen
            </h2>
            <div className="w-20 h-1 bg-victory-red rounded-full" data-id="element-245" />
            <div className="font-body text-lg text-charcoal/80 space-y-4 leading-relaxed" data-id="element-246">
              <p data-id="element-247">
                Victory Kitchen started as a humble catering business in Chef
                Victory's home kitchen. Five years later, fueled by passion and
                community support, it expanded into the vibrant restaurant space
                you see today.
              </p>
              <p data-id="element-248">
                Chef Victory combines knowledge passed down through generations
                of her family along with professional culinary training to
                create classic West African cuisine in a modern way.
              </p>
              <p data-id="element-249">
                Our food focuses on local base ingredients like tomatoes, leafy
                greens, and root vegetables, which are the heart of the rich
                soups and stews common in West African cuisine.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="relative rounded-3xl overflow-hidden shadow-2xl border border-amber/20" data-id="element-250">
            <img src="https://i0.wp.com/victorykitchenz.com/wp-content/uploads/2021/04/Meals-001-1.jpeg?fit=950%2C633&ssl=1" alt="Victory Kitchen Z Food Spread" className="w-full h-full object-cover min-h-[300px]" data-id="element-251" />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/70 to-charcoal/30" data-id="element-252" />
            <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-253">
              <h3 className="font-heading text-2xl text-white uppercase mb-3" data-id="element-254">
                Our Roots in Catering
              </h3>
              <p className="font-body text-white/80 mb-4" data-id="element-255">
                Chef Victoria started by catering events, and as demand grew,
                the restaurant was established. We still proudly cater weddings,
                birthday parties, office events, and church events.
              </p>
              <button onClick={() => onNavigate('catering')} className="bg-amber hover:bg-amber/90 text-charcoal font-heading px-6 py-3 rounded-xl transition-colors uppercase tracking-wider" data-id="element-256">
                Learn About Catering
              </button>
            </div>
          </motion.div>
        </div>

        {/* Features & Team */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-257">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-258">
            <h2 className="font-heading text-3xl text-charcoal uppercase tracking-wide mb-8" data-id="element-259">
              Restaurant Features
            </h2>
            <div className="grid grid-cols-2 gap-y-6 gap-x-4" data-id="element-260">
              {features.map(feature => <div key={feature} className="flex items-center space-x-3 bg-white p-4 rounded-xl shadow-sm border border-charcoal/5" data-id="element-261">
                  <div className="w-2 h-2 bg-amber rounded-full" data-id="element-262" />
                  <span className="font-heading text-lg text-charcoal uppercase tracking-wide" data-id="element-263">
                    {feature}
                  </span>
                </div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} data-id="element-264">
            <h2 className="font-heading text-3xl text-charcoal uppercase tracking-wide mb-8" data-id="element-265">
              Meet Our Chefs
            </h2>
            <div className="space-y-4" data-id="element-266">
              {team.map(member => <div key={member.name} className="flex items-center justify-between bg-charcoal text-white p-6 rounded-2xl" data-id="element-267">
                  <div className="flex items-center space-x-4" data-id="element-268">
                    <div className="w-12 h-12 bg-amber/20 rounded-full flex items-center justify-center text-amber font-heading text-xl" data-id="element-269">
                      {member.name.charAt(0)}
                    </div>
                    <span className="font-heading text-xl uppercase tracking-wide" data-id="element-270">
                      {member.name}
                    </span>
                  </div>
                  <span className="font-body text-amber text-sm uppercase tracking-widest" data-id="element-271">
                    {member.role}
                  </span>
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>;
}